import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Spinner,
  Alert,
} from "react-bootstrap";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
const API_URL = `${API_BASE}/api/epaper`;

const AdminEPaper = () => {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingDate, setDeletingDate] = useState(null);
  const [uploadDate, setUploadDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [uploadFile, setUploadFile] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchPapers();
  }, []);

  const fetchPapers = async () => {
    try {
      setLoading(true);
      const res = await fetch(API_URL);
      const result = await res.json();
      setPapers(result || []);
    } catch (err) {
      console.error(err);
      setError("Unable to load ePaper list. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadFile) {
      setError("Please choose a PDF file to upload.");
      setMessage("");
      return;
    }

    setUploading(true);
    setError("");
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("date", uploadDate);

      const res = await fetch(`${API_URL}/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Upload failed");
      }

      setMessage("E-paper uploaded successfully.");
      setUploadFile(null);
      setUploadDate(new Date().toISOString().slice(0, 10));
      fetchPapers();
    } catch (err) {
      console.error(err);
      setError(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (date) => {
    if (!window.confirm(`Delete ePaper for ${date}?`)) return;

    setDeletingDate(date);
    setMessage("");
    setError("");

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Session expired. Please login again.");
      }

      const res = await fetch(`${API_URL}/${date}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Delete failed");
      }

      setMessage("E-paper deleted successfully.");
      fetchPapers();
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to delete ePaper.");
    } finally {
      setDeletingDate(null);
    }
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col md={8}>
          <h2>🗞 Admin ePaper Management</h2>
          <p className="text-muted">
            Upload new ePaper PDFs and manage existing issues from the admin
            panel.
          </p>
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={5}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title>Upload New ePaper</Card.Title>
              <Card.Text>
                Choose a PDF file and set the issue date. The uploaded file will
                be available for users to view and download.
              </Card.Text>

              {message && <Alert variant="success">{message}</Alert>}
              {error && <Alert variant="danger">{error}</Alert>}

              <Form onSubmit={handleUpload}>
                <Form.Group className="mb-3" controlId="uploadDate">
                  <Form.Label>Issue Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={uploadDate}
                    onChange={(e) => setUploadDate(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="uploadFile">
                  <Form.Label>PDF File</Form.Label>
                  <Form.Control
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setUploadFile(e.target.files[0] || null)}
                    required
                  />
                </Form.Group>

                <Button type="submit" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      Uploading...
                    </>
                  ) : (
                    "Upload ePaper"
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title>Published ePapers</Card.Title>
              <Card.Text>
                View the current list of uploaded ePapers. Click the links to
                preview or download each issue.
              </Card.Text>

              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : papers.length === 0 ? (
                <Alert variant="info">No ePapers uploaded yet.</Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Preview</th>
                        <th>Download</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {papers.map((paper) => (
                        <tr key={paper.date}>
                          <td>{paper.date}</td>
                          <td>
                            <a
                              href={`${API_BASE}${paper.file}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View PDF
                            </a>
                          </td>
                          <td>
                            <a
                              href={`${API_BASE}${paper.file}`}
                              download
                            >
                              Download
                            </a>
                          </td>
                          <td>
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={deletingDate === paper.date}
                              onClick={() => handleDelete(paper.date)}
                            >
                              {deletingDate === paper.date
                                ? "Deleting..."
                                : "Delete"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminEPaper;
